/**
 * Home dashboard API client — Round J-2 (2026-06-05).
 *
 * The This Week widget reads from /api/home/this-week, which returns
 * three buckets pre-scoped to the calling agent. See
 * apps/api/src/routes/home.ts for the shape contract.
 */
import { apiFetch } from '../auth/api';

export interface ThisWeekCall {
  id: number;
  first_name: string;
  last_name: string | null;
  phone: string | null;
  secondary_phone: string | null;
  email: string | null;
  manual_added: number;
  last_contacted_at: number | null;
}

export interface ThisWeekTask {
  id: number;
  title: string;
  due_date: number;
  source: 'contact' | 'transaction';
  transaction_id: number | null;
  contact_id: number | null;
  address: string | null;
}

export interface ThisWeekAppointment {
  id: number;
  title: string;
  occurs_at: number;
  kind: 'open_house' | 'event' | 'task_appt';
  transaction_id: number | null;
  address: string | null;
}

export interface ThisWeekResponse {
  week_start: number;
  week_end: number;
  dtd2_group: string;
  calls: ThisWeekCall[];
  tasks: ThisWeekTask[];
  appointments: ThisWeekAppointment[];
}

export function getThisWeek(): Promise<ThisWeekResponse> {
  return apiFetch('/api/home/this-week');
}

// L-71 (Daniel 2026-07-04): compressed cross-deal view of every key
// date + commission. Powers the Deal Deadlines widget on Home.
export interface DealDeadline {
  id: number;
  type: 'buyer' | 'listing' | 'auction';
  status: string | null;
  address_street: string | null;
  address_city: string | null;
  address_state: string | null;
  primary_party_names: string | null;
  contract_date: number | null;
  close_date: number | null;
  inspection_deadline: number | null;
  appraisal_deadline: number | null;
  financing_deadline: number | null;
  walkthrough_date: number | null;
  earnest_money_cents: number | null;
  sale_price_cents: number | null;
  list_price_cents: number | null;
  /** Total commission %, e.g. 0.06 for 6%. */
  commission_pct: number | null;
  /** Buyer's-agent share when we're the listing side, e.g. 0.03. */
  buyer_agent_pct: number | null;
  /** Referral cut, e.g. 0.25. */
  referral_agent_pct: number | null;
  /** C-1 (Daniel 2026-08-09): 'flat' when the deal's commission is a dollar
   *  amount rather than a percentage. V-1: null for non-leadership. */
  commission_mode?: 'pct' | 'flat' | null;
  /** C-1: computed total commission in cents. Shape-independent, so it is
   *  right for flat-fee and multi-party deals too. Null when the deal has
   *  had no commission entered at all. */
  total_commission_cents?: number | null;
  /** C-1: what lands on the team's side after gross deductions, in cents. */
  team_commission_cents?: number | null;
  agent_name: string | null;
}

export interface DealDeadlinesResponse {
  deals: DealDeadline[];
  /**
   * V-1 (Daniel 2026-08-10): false for callers who are not leadership. When
   * false every commission field on every row is null and the widget drops the
   * Commission column rather than printing a dash per deal, which would read
   * as "nobody entered it" instead of "not yours to see".
   *
   * The deals themselves stay team-wide. This table is the huddle's deadline
   * coordination tool and that is deliberate.
   */
  commission_visible: boolean;
}

export function getDealDeadlines(): Promise<DealDeadlinesResponse> {
  return apiFetch('/api/home/deal-deadlines');
}

// L-102 (Daniel 2026-07-08): "what happened lately" digest — team-wide.
export interface RecentActivityWindow {
  emails_sent: number;
  texts_sent: number;
  tasks_completed: number;
  new_leads: number;
  deals_touched: number;
}
export interface RecentActivityResponse {
  day: RecentActivityWindow;
  week: RecentActivityWindow;
}
export function getRecentActivity(): Promise<RecentActivityResponse> {
  return apiFetch('/api/home/recent-activity');
}

// ─────────────────────────────────────────────────────────────────────
// R-1 (Daniel 2026-08-10): pipeline report. Pending dollars, expected
// closings for the week / month / next month, and the last 7 days of
// leads with the appointments they produced. One fetch, because the
// dashboard asks all of it at once.
//
// Every *_cents field is cents, formatted with formatMoney().
// ─────────────────────────────────────────────────────────────────────
export interface PipelineCountVolume {
  count: number;
  volume_cents: number;
}

export interface PendingAgentRow {
  user_id: number | null;
  /** "Unassigned" when a deal has no primary agent. */
  name: string;
  count: number;
  volume_cents: number;
}

export interface PipelineReportResponse {
  windows: {
    week_start: number;
    week_end: number;
    month_start: number;
    month_end: number;
    next_month_start: number;
    next_month_end: number;
  };
  /**
   * V-1 (Daniel 2026-08-10): which deals the dollars below cover. 'team' for
   * owner / operations / admin, 'own' for everyone else, who now sees only the
   * deals they are on. Covers pending_volume AND closings_forecast, because a
   * single ownership predicate scopes both. The cards put it in the label so a
   * smaller number arrives with its reason.
   */
  money_scope: 'team' | 'own';
  pending_volume: {
    count: number;
    volume_cents: number;
    by_agent: PendingAgentRow[];
    /** False for anyone who is not owner or operations. The server sends an
     *  empty by_agent in that case, so this distinguishes "not allowed" from
     *  "nothing under contract". */
    by_agent_visible: boolean;
  };
  /** R-2 (Daniel 2026-08-31): active book on the market (list/target price of
   *  status='active' deals), same money scope as pending_volume. Feeds the
   *  home Forecast pipeline roll-up. */
  active_volume: {
    count: number;
    volume_cents: number;
  };
  /** R-2 (Daniel 2026-08-31): potential commissions NET TO THE COMPANY. The
   *  summed per-deal house_commission (what the team keeps after co-op,
   *  referrals and the agent split) for active and under-contract deals. The
   *  lead piece is estimated on the web from the weighted lead forecast. */
  potential_commissions: {
    active_cents: number;
    pending_cents: number;
  };
  /** One-side team commission rate (teams.default_commission_rate, fallback
   *  0.03), used only for the lead estimate. */
  commission_rate: number;
  /** Company's share of commission (1 - teams.default_agent_split_pct,
   *  fallback 0.40), used only for the lead estimate. */
  company_share: number;
  closings_forecast: {
    this_week: PipelineCountVolume;
    this_month: PipelineCountVolume;
    next_month: PipelineCountVolume;
  };
  lead_funnel_week: {
    days: number;
    total_leads: number;
    from_ads: number;
    appointments_set: number;
    appointments_held: number;
    from_ads_appointments_set: number;
    /** Lead-to-appointment percentage, one decimal. */
    conversion_rate: number;
    /** Null when no appointment has ever been logged, which means the
     *  conversion rate above is not measuring anything yet. */
    appointments_tracked_since: number | null;
  };
}

export function getPipelineReport(): Promise<PipelineReportResponse> {
  return apiFetch('/api/home/pipeline-report');
}
